/**
 * services/api.js
 *
 * API service for Speech Club platform.
 * Centralizes all API calls with consistent error handling.
 */

import { catalystFetch, getCatalystAuthToken } from './catalystFetch';

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/server/processMedia/api'
  : '/api';

/**
 * XHR-based upload that supports upload progress callbacks.
 * Use this instead of apiFetch when you need onProgress (e.g. large file uploads).
 *
 * @param {string}   endpoint   - API path, e.g. '/speeches/123/upload-audio'
 * @param {FormData} formData
 * @param {function} [onProgress] - (percent: number) => void, called during upload
 * @returns {Promise<object>}    Parsed JSON response
 */
export async function apiUpload(endpoint, formData, onProgress) {
  const url = `${API_BASE}${endpoint}`;
  let authToken = null;
  try { authToken = await getCatalystAuthToken(); } catch (_) { /* handled as 401 */ }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    if (authToken) xhr.setRequestHeader('Authorization', authToken);

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    }

    xhr.onload = () => {
      let data;
      try { data = JSON.parse(xhr.responseText); } catch {
        reject(Object.assign(new Error('Server returned invalid response'), { code: 'PARSE_ERROR' }));
        return;
      }
      if (xhr.status === 401) { reject(Object.assign(new Error('Authentication required'), { code: 'AUTHENTICATION_REQUIRED' })); return; }
      if (xhr.status === 403) { reject(Object.assign(new Error(data?.message || 'Access forbidden'), { code: data?.error || 'FORBIDDEN' })); return; }
      if (xhr.status === 413) { reject(Object.assign(new Error('File is too large. Maximum size is 100 MB.'), { code: 'FILE_TOO_LARGE' })); return; }
      if (!data?.success) { reject(Object.assign(new Error(data?.message || 'Upload failed'), { code: data?.error || 'UPLOAD_FAILED' })); return; }
      resolve(data);
    };

    xhr.onerror = () => reject(Object.assign(new Error('Network error during upload'), { code: 'NETWORK_ERROR' }));
    xhr.ontimeout = () => reject(Object.assign(new Error('Upload timed out'), { code: 'UPLOAD_TIMEOUT' }));
    xhr.timeout = 10 * 60 * 1000; // 10 minutes for large files

    xhr.send(formData);
  });
}

/**
 * Base fetch wrapper with credentials and error handling.
 */
async function apiFetch(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  const config = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    signal: controller.signal,
    ...options,
  };

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  let res;
  try {
    res = await catalystFetch(url, config);
  } catch (fetchErr) {
    clearTimeout(timeoutId);
    if (fetchErr.name === 'AbortError') {
      const error = new Error('Could not load — check your connection.');
      error.code = 'TIMEOUT';
      throw error;
    }
    const error = new Error('Network error - is the backend server running?');
    error.code = 'NETWORK_ERROR';
    throw error;
  }
  clearTimeout(timeoutId);

  if (res.status === 401) {
    const error = new Error('Authentication required');
    error.code = 'AUTHENTICATION_REQUIRED';
    throw error;
  }

  if (res.status === 403) {
    // Parse the response to preserve specific error codes (MEMBERSHIP_PENDING, etc.)
    const ct = res.headers.get('content-type');
    if (ct && ct.includes('application/json')) {
      const data = await res.json();
      const error = new Error(data.message || 'Access forbidden');
      error.code = data.error || 'FORBIDDEN';
      throw error;
    }
    const error = new Error('Access forbidden');
    error.code = 'FORBIDDEN';
    throw error;
  }

  // Handle non-JSON responses (e.g., proxy errors)
  const contentType = res.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await res.text();
    const error = new Error(text.includes('Proxy error')
      ? 'Backend server not running - start it on port 9000'
      : `Unexpected response: ${text.slice(0, 100)}`);
    error.code = 'INVALID_RESPONSE';
    throw error;
  }

  const data = await res.json();

  if (!res.ok) {
    const error = new Error(data.message || `HTTP ${res.status}`);
    error.code = data.error || 'UNKNOWN_ERROR';
    throw error;
  }

  return data;
}

// ══════════════════════════════════════════════════════════════════════════
// Member APIs
// ══════════════════════════════════════════════════════════════════════════

export async function getCurrentMember() {
  return apiFetch('/members/me');
}

export async function getMembers(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.role) query.set('role', params.role);
  if (params.includeInactive) query.set('includeInactive', 'true');
  const qs = query.toString();
  return apiFetch(`/members${qs ? `?${qs}` : ''}`);
}

export async function getMembersWithStats(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.includeInactive) query.set('includeInactive', 'true');
  const qs = query.toString();
  return apiFetch(`/members/with-stats${qs ? `?${qs}` : ''}`);
}

export async function updateMemberRole(memberId, role) {
  return apiFetch(`/members/${memberId}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

export async function approveMember(memberId) {
  return apiFetch(`/members/${memberId}/approve`, {
    method: 'PATCH',
  });
}

export async function rejectMember(memberId) {
  return apiFetch(`/members/${memberId}/reject`, {
    method: 'PATCH',
  });
}

export async function setMemberActive(memberId, isActive) {
  return apiFetch(`/members/${memberId}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ isActive }),
  });
}

// ══════════════════════════════════════════════════════════════════════════
// Settings APIs
// ══════════════════════════════════════════════════════════════════════════

export async function getSettings() {
  return apiFetch('/settings');
}

export async function updateSettings(updates) {
  return apiFetch('/settings', {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

// ══════════════════════════════════════════════════════════════════════════
// Meeting APIs
// ══════════════════════════════════════════════════════════════════════════

export async function getMeetings(type = 'upcoming', limit = 10) {
  return apiFetch(`/meetings?type=${type}&limit=${limit}`);
}

export async function getMeeting(meetingId) {
  return apiFetch(`/meetings/${meetingId}`);
}

export async function createMeeting(meeting_date, title, meeting_number) {
  return apiFetch('/meetings', {
    method: 'POST',
    body: JSON.stringify({ meeting_date, title, ...(meeting_number !== undefined ? { meeting_number } : {}) }),
  });
}

export async function updateMeeting(meetingId, updates) {
  return apiFetch(`/meetings/${meetingId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteMeeting(meetingId) {
  return apiFetch(`/meetings/${meetingId}`, { method: 'DELETE' });
}

export async function autoGenerateMeetings(weeks) {
  return apiFetch('/meetings/auto-generate', {
    method: 'POST',
    body: JSON.stringify({ weeks }),
  });
}

// ══════════════════════════════════════════════════════════════════════════
// Slot APIs
// ══════════════════════════════════════════════════════════════════════════

export async function bookSlot(slotId, { presentation_title, synopsis } = {}) {
  return apiFetch(`/slots/${slotId}/book`, {
    method: 'POST',
    body: JSON.stringify({ presentation_title, synopsis }),
  });
}

export async function cancelSlotBooking(slotId) {
  return apiFetch(`/slots/${slotId}/book`, {
    method: 'DELETE',
  });
}

export async function getMySlots() {
  return apiFetch('/slots/my');
}

export async function assignSlot(slotId, { speaker_id, presentation_title, synopsis }) {
  return apiFetch(`/slots/${slotId}/assign`, {
    method: 'PATCH',
    body: JSON.stringify({ speaker_id, presentation_title, synopsis }),
  });
}

export async function addMeetingSlot(meetingId) {
  return apiFetch(`/meetings/${meetingId}/slots`, { method: 'POST' });
}

export async function deleteMeetingSlot(slotId) {
  return apiFetch(`/slots/${slotId}`, { method: 'DELETE' });
}

export async function unassignSlot(slotId, force = false) {
  const url = force ? `/slots/${slotId}/assign?force=true` : `/slots/${slotId}/assign`;
  return apiFetch(url, { method: 'DELETE' });
}

// ══════════════════════════════════════════════════════════════════════════
// Speech APIs
// ══════════════════════════════════════════════════════════════════════════

export async function getSpeeches(type = 'public', limit = 20, offset = 0) {
  return apiFetch(`/speeches?type=${type}&limit=${limit}&offset=${offset}`);
}

export async function getSpeech(speechId) {
  return apiFetch(`/speeches/${speechId}`);
}

export function createSpeech(formData, { onProgress } = {}) {
  return apiUpload('/speeches', formData, onProgress);
}

export async function updateSpeech(speechId, updates) {
  return apiFetch(`/speeches/${speechId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteSpeech(speechId) {
  return apiFetch(`/speeches/${speechId}`, { method: 'DELETE' });
}

// Admin-only: add a video to the Library (no meeting/slot).
// Pass exactly one of speaker_id (existing member) or new_speaker
// ({ email, first_name, last_name }) — the latter creates a provisional member
// and sends them a club invitation.
export async function addLibraryVideo({ title, synopsis, youtube_url, speaker_id, new_speaker }) {
  return apiFetch('/library-videos', {
    method: 'POST',
    body: JSON.stringify({ title, synopsis, youtube_url, speaker_id, new_speaker }),
  });
}

// Re-runs AI coaching + content analysis on the stored transcript (no re-transcription).
// Backfills the Content & Ideas section on older speeches. Admin only.
export async function regenerateSpeechFeedback(speechId) {
  return apiFetch(`/speeches/${speechId}/regenerate-feedback`, { method: 'POST' });
}

export async function clearSpeechAudio(speechId) {
  return apiFetch(`/speeches/${speechId}/audio`, { method: 'DELETE' });
}

export function uploadSpeechAudio(speechId, file, { duration = 0, onProgress } = {}) {
  const formData = new FormData();
  formData.append('audio', file);
  if (duration > 0) formData.append('duration', String(duration));
  return apiUpload(`/speeches/${speechId}/upload-audio`, formData, onProgress);
}

export async function analyzeSpeechFromUrl(speechId, audioUrl) {
  return apiFetch(`/speeches/${speechId}/analyze-url`, {
    method: 'POST',
    body: JSON.stringify({ audio_url: audioUrl }),
  });
}

export async function toggleSpeechVisibility(speechId, isPublic) {
  return apiFetch(`/speeches/${speechId}/visibility`, {
    method: 'PATCH',
    body: JSON.stringify({ is_public: isPublic }),
  });
}

// ══════════════════════════════════════════════════════════════════════════
// Feedback APIs
// ══════════════════════════════════════════════════════════════════════════

export async function getSpeechFeedback(speechId) {
  return apiFetch(`/speeches/${speechId}/feedback`);
}

export async function submitFeedback(speechId, content) {
  return apiFetch(`/speeches/${speechId}/feedback`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

export async function updateFeedback(feedbackId, content) {
  return apiFetch(`/feedback/${feedbackId}`, {
    method: 'PATCH',
    body: JSON.stringify({ content }),
  });
}

export async function deleteFeedback(feedbackId) {
  return apiFetch(`/feedback/${feedbackId}`, {
    method: 'DELETE',
  });
}

// ══════════════════════════════════════════════════════════════════════════
// Invitation APIs
// ══════════════════════════════════════════════════════════════════════════

export async function createInvitation(email, role) {
  return apiFetch('/invitations', {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

export async function getInvitations(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.role) query.set('role', params.role);
  const qs = query.toString();
  return apiFetch(`/invitations${qs ? `?${qs}` : ''}`);
}

export async function resendInvitation(invitationId) {
  return apiFetch(`/invitations/${invitationId}/resend`, {
    method: 'POST',
  });
}

export async function revokeInvitation(invitationId) {
  return apiFetch(`/invitations/${invitationId}`, {
    method: 'DELETE',
  });
}

export async function getInvitationByToken(token) {
  return apiFetch(`/invitations/token/${token}`);
}

export async function acceptInvitation(token) {
  return apiFetch(`/invitations/token/${token}/accept`, {
    method: 'POST',
  });
}

// ══════════════════════════════════════════════════════════════════════════
// Gamification APIs
// ══════════════════════════════════════════════════════════════════════════

export async function generateCheckinToken(meetingId) {
  return apiFetch(`/meetings/${meetingId}/checkin-token`, {
    method: 'POST',
  });
}

export async function checkin(token) {
  return apiFetch(`/checkin/${token}`, {
    method: 'POST',
  });
}

export async function getMeetingAttendance(meetingId) {
  return apiFetch(`/meetings/${meetingId}/attendance`);
}

export async function markAttendanceManual(meetingId, memberId) {
  return apiFetch(`/meetings/${meetingId}/attendance/${memberId}`, {
    method: 'POST',
  });
}

export async function getLeaderboard(period = 'all', limit = 50) {
  const params = new URLSearchParams();
  if (period && period !== 'all') params.set('period', period);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return apiFetch(`/leaderboard${qs ? `?${qs}` : ''}`);
}

export async function getMyPoints() {
  return apiFetch('/points/me');
}

export async function awardSpeechPoints(speechId) {
  return apiFetch(`/speeches/${speechId}/award-points`, { method: 'POST' });
}

// ══════════════════════════════════════════════════════════════════════════
// Progress APIs
// ══════════════════════════════════════════════════════════════════════════

export async function getMyProgress() {
  return apiFetch('/progress/me');
}

export async function getMemberProgress(memberId) {
  return apiFetch(`/progress/${memberId}`);
}

// ══════════════════════════════════════════════════════════════════════════
// Join Request APIs
// ══════════════════════════════════════════════════════════════════════════

export async function submitJoinRequest(reason) {
  return apiFetch('/join-requests', {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function getJoinRequests(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  const qs = query.toString();
  return apiFetch(`/join-requests${qs ? `?${qs}` : ''}`);
}

export async function approveJoinRequest(requestId) {
  return apiFetch(`/join-requests/${requestId}/approve`, {
    method: 'PATCH',
  });
}

export async function rejectJoinRequest(requestId) {
  return apiFetch(`/join-requests/${requestId}/reject`, {
    method: 'PATCH',
  });
}

export async function getAdminErrors(params = {}) {
  const query = new URLSearchParams();
  if (params.service) query.set('service', params.service);
  if (params.severity) query.set('severity', params.severity);
  if (params.limit) query.set('limit', params.limit);
  const qs = query.toString();
  return apiFetch(`/admin/errors${qs ? `?${qs}` : ''}`);
}

export async function clearAdminErrors() {
  return apiFetch('/admin/errors', { method: 'DELETE' });
}

// ── App Feedback APIs ─────────────────────────────────────────────────────────

export async function submitAppFeedback(content, isAnonymous = false) {
  return apiFetch('/app-feedback', {
    method: 'POST',
    body: JSON.stringify({ content, is_anonymous: isAnonymous }),
  });
}

export async function getAppFeedback(limit = 50) {
  return apiFetch(`/app-feedback?limit=${limit}`);
}

// ══════════════════════════════════════════════════════════════════════════
// Friends APIs
// ══════════════════════════════════════════════════════════════════════════

export async function requestFriend(addresseeId) {
  return apiFetch('/friend-requests', {
    method: 'POST',
    body: JSON.stringify({ addressee_id: addresseeId }),
  });
}

export async function getFriendRequests(params = {}) {
  const query = new URLSearchParams();
  if (params.direction) query.set('direction', params.direction);
  const qs = query.toString();
  return apiFetch(`/friend-requests${qs ? `?${qs}` : ''}`);
}

export async function acceptFriendRequest(friendshipId) {
  return apiFetch(`/friend-requests/${friendshipId}/accept`, { method: 'PATCH' });
}

export async function rejectFriendRequest(friendshipId) {
  return apiFetch(`/friend-requests/${friendshipId}/reject`, { method: 'PATCH' });
}

export async function getFriends() {
  return apiFetch('/friends');
}

export async function unfriend(memberId) {
  return apiFetch(`/friends/${memberId}`, { method: 'DELETE' });
}

// Member-visible roster. Deliberately carries NO email addresses — the admin
// roster (getMembers) does, and this one is open to every approved member.
export async function getPeople() {
  return apiFetch('/people');
}

/**
 * Fetch a locked speech's poster frame as an object URL.
 *
 * Why not just `<img src="/api/speeches/:id/thumbnail">`: an <img> tag cannot
 * carry the `Authorization: Zoho-oauthtoken` header, and this Advanced I/O
 * function requires the bearer token — a cookie alone gets 401 (ADR-037,
 * AUTH.md rule 2). So we fetch the bytes through catalystFetch, which attaches
 * the token, and hand the caller a blob URL instead.
 *
 * Caller MUST URL.revokeObjectURL() the result on unmount or it leaks.
 *
 * @param {string} speechId
 * @returns {Promise<string|null>} object URL, or null when there's no poster
 */
export async function fetchSpeechThumbnail(speechId) {
  const url = `${API_BASE}/speeches/${speechId}/thumbnail`;
  const res = await catalystFetch(url, { credentials: 'include' });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ── Member video upload (Stratus) ───────────────────────────────────────────
// The bytes go browser → Stratus on a signed URL; this function only signs and
// records. See functions/processMedia/routes/memberVideos.js.

export async function createMyVideo({ title, synopsis, mime_type, duration_secs }) {
  return apiFetch('/my-videos', {
    method: 'POST',
    body: JSON.stringify({ title, synopsis, mime_type, duration_secs }),
  });
}

export async function completeMyVideo(speechId) {
  return apiFetch(`/my-videos/${speechId}/complete`, { method: 'POST' });
}

/** Signed, short-lived playback URL for an uploaded video (403 for non-friends). */
export async function getSpeechMediaUrl(speechId) {
  return apiFetch(`/speeches/${speechId}/media-url`);
}

/**
 * PUT a file to a signed Stratus URL with upload progress.
 *
 * Deliberately NOT catalystFetch: Stratus is another origin, so our Zoho bearer
 * token must never be attached (it would leak to another host and fail CORS).
 * The signed URL is its own credential.
 */
export function putToSignedUrl(url, body, { contentType, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(Object.assign(new Error(`Upload failed (${xhr.status})`), { code: 'UPLOAD_FAILED' })));
    xhr.onerror = () => reject(Object.assign(new Error('Upload blocked or network error'), { code: 'UPLOAD_FAILED' }));
    xhr.send(body);
  });
}

export { API_BASE };
